import { Modal, notification } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { forwardRef, useImperativeHandle, useState } from 'react';
import styled from 'styled-components';
import { LanguageDataDictionary } from 'src/service';
import { Card, YgoproDeckCard } from 'src/model';
import { CardSearch } from 'src/component/card-search';
import { StyledActionIconButton } from './styled';

const StyledSearchContainer = styled.div`
    .search-description {
        margin-bottom: var(--spacing);
        color: var(--color-contrast-80);
        font-size: var(--fs);
    }
`;

export type SearchPanelRef = {
    requestSearch: () => void,
};

export type SearchPanelProps = {
    language: LanguageDataDictionary,
    onImport: (
        data: Card,
        option?: {
            forcePurityCheck?: boolean,
            writeOnCurrentCard?: boolean,
            addToList?: boolean,
        },
    ) => void,
    onClose: () => void,
};

export const SearchPanel = forwardRef<SearchPanelRef, SearchPanelProps>(({
    language,
    onImport,
    onClose,
}, ref) => {
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    useImperativeHandle(ref, () => ({
        requestSearch: () => {
            setVisible(true);
        }
    }));

    const cleanup = () => {
        setVisible(false);
        setLoading(false);
        onClose();
    };

    const handleCardSelect = async (card: YgoproDeckCard) => {
        setLoading(true);
        try {
            const { ygoproDeckToYgoCarderData } = await import('src/util/codec-ygoprodeck');
            const { result } = ygoproDeckToYgoCarderData(card);
            
            onImport(result, {
                forcePurityCheck: true,
                writeOnCurrentCard: true,
                addToList: false,
            });
            
            notification.success({
                message: language['prompt.search.success.message'] || 'Card imported successfully',
                description: `${card.name} - To download, right-click and select "Save image as..."`,
            });
            
            cleanup();
        } catch (error) {
            console.error('Import error:', error);
            notification.error({
                message: language['error.import.error.message'],
                description: language['error.import.error.description'],
            });
            setLoading(false);
        }
    };

    return (
        <>
            <Modal
                visible={visible}
                title={language['button.search.label'] || 'Search Card'}
                className="global-overlay search-modal"
                onCancel={cleanup}
                footer={null}
                destroyOnClose={true}
                width={600}
            >
                <StyledSearchContainer>
                    <div className="search-description">
                        {language['prompt.search.description'] || 'Search for any Yu-Gi-Oh card from the YGOPRODeck database and import it directly into the editor.'}
                    </div>
                    <CardSearch
                        onSelect={handleCardSelect}
                        placeholder={language['prompt.import.search.placeholder'] || 'Search for a Yu-Gi-Oh card...'}
                        disabled={loading}
                    />
                </StyledSearchContainer>
            </Modal>
            <StyledActionIconButton
                className="secondary-button search-button"
                onClick={() => setVisible(true)}
                title={language['button.search.tooltip'] || 'Search and Import Card'}
            >
                <SearchOutlined />
            </StyledActionIconButton>
        </>
    );
});
